// Lib
import { FC } from "react";
import { useNavigate } from "react-router-dom";
// Types
import {
  ChargeFailureReasonLabel,
  ChargeStatusLabel,
  OrderPaymentType,
  OrderStatus,
  OrderStatusLabel,
} from "types/orders";
import { SummaryProps } from "./types";
// Theme
// Constants
import { ADMIN_ROUTES } from "consts";
// Helpers
import {
  convertToTitleCase,
  dateTransform,
  estimatedDeliveryTimeCreate,
  fixedDigitsValue,
} from "helpers/dataHelpers";
// Icons
import { PersonIcon, LocationsIcon } from "icons";
// Components
import { Badge, FilledButtonLink, SummaryField } from "components";
// Styled
import { ContentBox, FlexContainer, SummaryItemsGrid } from "styled/Box";
import { Typography } from "styled/Typography";

export const Summary: FC<SummaryProps> = ({ isLoading, isScheduled, data }) => {
  const {
    coupons,
    paymentType,
    fullName,
    sid,
    chargeStatus,
    orderStatus,
    chargeFailureReason,
    delivery,
    totalFmcCentsPrice,
    totalFiatCentsPrice,
    locationId,
    locationName,
    timeslot,
    customerId,
    expectedPreorderDeliveryTime,
    deliveredAt,
  } = data || {};

  const navigate = useNavigate();

  const handleOpenCustomerProfile = () => {
    navigate(`${ADMIN_ROUTES.CUSTOMERS.path}/${customerId}`);
  };

  const handleOpenCouponDetails = (id: string) => {
    navigate(`${ADMIN_ROUTES.COUPONS.path}/${id}`);
  };

  const handleOpenLocation = () => {
    navigate(`${ADMIN_ROUTES.DELIVERY_AREAS.path}/${locationId}`);
  };

  return (
    <ContentBox $column $gap={20}>
      <Typography.H2>Summary</Typography.H2>

      <SummaryItemsGrid>
        <SummaryField isLoading={isLoading} label="Order number" value={sid} />

        <SummaryField
          isLoading={isLoading}
          label="Customer"
          value={
            <FilledButtonLink
              icon={<PersonIcon />}
              title={fullName || customerId}
              onClick={handleOpenCustomerProfile}
            />
          }
        />

        <SummaryField
          isLoading={isLoading}
          label="Order status"
          value={
            orderStatus === OrderStatus.DELIVERED ? (
              <Badge title={OrderStatusLabel[orderStatus]} />
            ) : (
              <Badge
                status={isScheduled && orderStatus}
                title={OrderStatusLabel[orderStatus]}
              />
            )
          }
        />

        <SummaryField
          isLoading={isLoading}
          label="Charge status"
          value={
            chargeStatus && <Badge title={ChargeStatusLabel[chargeStatus]} />
          }
        />

        {isScheduled && (
          <>
            <SummaryField
              isLoading={isLoading}
              label="Scheduled at"
              value={dateTransform({ date: expectedPreorderDeliveryTime })}
            />
            <SummaryField
              isLoading={isLoading}
              label="Delivered At"
              value={dateTransform({ date: deliveredAt })}
            />
          </>
        )}

        <SummaryField
          isLoading={isLoading}
          label="Delivery partner"
          value={
            delivery?.deliveryProvider &&
            convertToTitleCase(delivery.deliveryProvider)
          }
        />

        {paymentType === OrderPaymentType.FiatCents ? (
          <SummaryField
            isLoading={isLoading}
            label="KWD"
            value={fixedDigitsValue(totalFiatCentsPrice, 3)}
          />
        ) : (
          <SummaryField
            isLoading={isLoading}
            label="FM coins"
            value={fixedDigitsValue(totalFmcCentsPrice, 3)}
          />
        )}

        {paymentType === OrderPaymentType.FiatCents ? (
          <SummaryField
            isLoading={isLoading}
            label="Delivery Fee (KWD)"
            value={fixedDigitsValue(delivery?.deliveryFeeFiatCents, 3)}
          />
        ) : (
          <SummaryField
            isLoading={isLoading}
            label="Delivery Fee (FM coins)"
            value={fixedDigitsValue(delivery?.deliveryFeeFmcCents, 3)}
          />
        )}

        <SummaryField
          isLoading={isLoading}
          label="Location"
          value={
            <FilledButtonLink
              icon={<LocationsIcon />}
              title={locationName}
              onClick={handleOpenLocation}
            />
          }
        />

        <SummaryField
          isLoading={isLoading}
          label="Selected Time slot"
          value={timeslot || null}
        />

        <SummaryField
          isLoading={isLoading}
          label="Estimated delivery time"
          value={estimatedDeliveryTimeCreate(
            delivery?.estimatedDeliveryTime,
            delivery?.estimatedDeliveryTimeEnd,
          )}
        />

        {chargeFailureReason && (
          <SummaryField
            isLoading={isLoading}
            label="Charge failure reason"
            value={
              <Badge title={ChargeFailureReasonLabel[chargeFailureReason]} />
            }
          />
        )}

        {!!coupons?.length && (
          <SummaryField
            isLoading={isLoading}
            label="Coupon code"
            value={
              <FlexContainer $gap={4}>
                {coupons.map(c => (
                  <FilledButtonLink
                    key={c.id}
                    title={c.couponCode}
                    onClick={() => handleOpenCouponDetails(c.id)}
                  />
                ))}
              </FlexContainer>
            }
          />
        )}
      </SummaryItemsGrid>
    </ContentBox>
  );
};
